import {
  BoxGeometry,
  BufferAttribute,
  BufferGeometry,
  Group,
  Mesh,
  MeshBasicMaterial,
} from 'three';
import type { HouseBodyConfig, StairConfig } from '@/model/schema';
import type { MaterialKit } from '../materials';

/** Residential stair geometry (meters). Building-code compliant treads are
 *  typically 250–280 mm deep with 175–200 mm risers. We compute the count
 *  from the target step rise so the geometry always lands on a plausible
 *  tread spacing, regardless of the flight's plan-view depth. */
const TARGET_STEP_RISE = 0.175;
const MIN_TREAD_DEPTH = 0.25;
const TREAD_THICKNESS = 0.04;

/** Returns the world-Y of the top surface of `level`'s floor slab in
 *  body-local coords (no plinth lift). Mirrors the slab placement in
 *  buildStory.ts so the stair sits flush. */
function floorTopY(body: HouseBodyConfig, level: number): number {
  if (level <= 0) return body.floorThickness;
  return level * body.storyHeight + body.floorThickness / 2;
}

/**
 * Build a single stair flight that climbs from `body.floorThickness` (top
 * of the lower story's floor slab) up to the upper story's floor slab top.
 *
 * The flight is rendered as a simple ramp of treads (no risers). It's
 * good enough to read as a stair in cutaway / peel views.
 *
 * Geometry is authored in BODY-LOCAL coords (lower-left at body origin
 * (originX, originZ)). The caller positions the flight in world space.
 */
export function buildStairFlight(
  body: HouseBodyConfig,
  stair: StairConfig,
  kit: MaterialKit,
): Group {
  const group = new Group();
  group.name = `Stairs_to_L${stair.toLevel}`;

  // The slab convention differs between ground (centred at +floorThickness/2,
  // top at +floorThickness) and upper stories (centred at +0 in story-local,
  // i.e. top at +floorThickness/2 above the elevation). buildStory.ts is the
  // source of truth — see the `isGround` branch there. If we use the wrong
  // formula for the destination (upperFloorTop), the top tread sits roughly
  // floorThickness/2 above the actual upper-floor top — image 5 in the
  // user's report.
  const lowerFloorTop = floorTopY(body, stair.toLevel - 1);
  const upperFloorTop = floorTopY(body, stair.toLevel);
  const climbHeight = upperFloorTop - lowerFloorTop;

  // Tread depth runs along the climb axis. We compute count so the flight
  // exactly fills `stair.depth` along the climb.
  const climbAxisLen = stair.climbDir === 'plusX' || stair.climbDir === 'minusX'
    ? stair.width
    : stair.depth;
  const lateralLen = stair.climbDir === 'plusX' || stair.climbDir === 'minusX'
    ? stair.depth
    : stair.width;
  // Pick a step count that gives a realistic rise; fall back to
  // a depth-based count only if the flight is too short to satisfy
  // the target rise. Either way, honour `climbAxisLen` so the flight
  // fills the planned footprint exactly.
  const riseBasedCount = Math.max(2, Math.round(climbHeight / TARGET_STEP_RISE));
  const depthBasedMax = Math.max(2, Math.floor(climbAxisLen / MIN_TREAD_DEPTH));
  const count = Math.min(riseBasedCount, depthBasedMax);
  const stepDepth = climbAxisLen / count;
  const stepRise = climbHeight / count;

  for (let i = 0; i < count; i++) {
    const tread = new Mesh(
      new BoxGeometry(
        stair.climbDir === 'plusX' || stair.climbDir === 'minusX' ? stepDepth : lateralLen,
        TREAD_THICKNESS,
        stair.climbDir === 'plusX' || stair.climbDir === 'minusX' ? lateralLen : stepDepth,
      ),
      kit.stair,
    );
    tread.name = `Tread_${i.toString().padStart(3, '0')}`;
    tread.castShadow = true;
    tread.receiveShadow = true;
    // Individual treads are VISUAL only: the ego-mode collider talks to
    // the hidden ramp added below instead. Marking them here (rather
    // than in the octree by name) keeps the collision policy close to
    // the geometry it applies to — easier to reason about when the
    // stair builder changes.
    tread.userData.collisionIgnore = true;
    // Bottom of tread i sits at lowerFloorTop + i * stepRise; tread is thin so we
    // place its centre slightly above.
    const yCentre = lowerFloorTop + (i + 1) * stepRise - TREAD_THICKNESS / 2;
    let xCentre = stair.x + stair.width / 2;
    let zCentre = stair.z + stair.depth / 2;
    switch (stair.climbDir) {
      case 'plusX':
        xCentre = stair.x + (i + 0.5) * stepDepth;
        break;
      case 'minusX':
        xCentre = stair.x + stair.width - (i + 0.5) * stepDepth;
        break;
      case 'plusZ':
        zCentre = stair.z + (i + 0.5) * stepDepth;
        break;
      case 'minusZ':
        zCentre = stair.z + stair.depth - (i + 0.5) * stepDepth;
        break;
    }
    // Convert body-local to story-centred (matching the wall-builder convention).
    tread.position.set(
      -body.footprintWidth / 2 + xCentre,
      yCentre,
      -body.footprintDepth / 2 + zCentre,
    );
    group.add(tread);
  }

  // Invisible collision ramp — a single slab whose TOP is the linear
  // interpolation from the bottom of the flight to the top. The ego
  // controller's capsule treats this as a smooth slope: the step-up
  // solver never fires because pushes off the ramp have a mostly-+Y
  // normal, so the player just glides up. Treads remain visible for
  // the eye; only this invisible proxy carries the collision.
  const ramp = buildStairRamp({
    body,
    stair,
    lowerFloorTop,
    upperFloorTop,
    lateralLen,
  });
  group.add(ramp);

  return group;
}

/**
 * Build the invisible collision proxy for a stair flight. The proxy is
 * a triangular-prism-shaped slab whose top face is a flat ramp from
 * lowerFloorTop at the flight entry to upperFloorTop at the flight
 * exit, and whose bottom is a horizontal slab at lowerFloorTop so the
 * ramp can't be "walked under". Width matches the flight's lateral
 * extent.
 *
 * Not exported — it's an implementation detail of buildStairFlight.
 */
function buildStairRamp(args: {
  body: HouseBodyConfig;
  stair: StairConfig;
  lowerFloorTop: number;
  upperFloorTop: number;
  lateralLen: number;
}): Mesh {
  const { body, stair, lowerFloorTop, upperFloorTop, lateralLen } = args;
  // Work out the four corners of the ramp's top face in body-local
  // coords, then the four matching bottom corners (at lowerFloorTop).
  // The ramp runs the full length of the stair footprint along its
  // climb axis, and spans `lateralLen` across.
  const sx = stair.x;
  const sz = stair.z;
  const sw = stair.width;
  const sd = stair.depth;
  // "entry" = the lower end of the flight; "exit" = the upper end.
  // Axes depend on climbDir.
  //
  // Local XZ layout (body-local, before the centring shift):
  //
  //   +X → / +Z ↓      entry                exit
  //   plusX:   x=sx, z=[sz, sz+sd]    x=sx+sw, z=[sz, sz+sd]   (low→high along +X)
  //   minusX:  x=sx+sw, ...           x=sx, ...                (low→high along -X)
  //   plusZ:   z=sz, x=[sx, sx+sw]    z=sz+sd, x=[sx, sx+sw]   (low→high along +Z)
  //   minusZ:  z=sz+sd, ...           z=sz, ...                (low→high along -Z)
  //
  // Eight vertices: 0..3 are TOP face (with the slope), 4..7 are
  // BOTTOM face (flat at lowerFloorTop). Index buffer assembles a
  // closed hull so the octree sees triangles on every side.
  //
  // INVARIANT: corners 0..3 must be CCW when viewed from +Y. The top
  // face is triangulated as (0,1,2),(0,2,3) — both CCW-from-above —
  // so the top normal always points roughly +Y. The bottom face uses
  // the mirrored winding (4,6,5),(4,7,6) so its normal points -Y, and
  // the four side faces wrap between them with outward normals.
  //
  // Getting this wrong for a single climbDir (it was wrong for
  // `minusX` and `plusZ` on the first cut) means the SOLVER pushes
  // the player DOWN into the floor every time they approach such a
  // ramp, which in practice looked like "the ground plane sometimes
  // disappears" in the user's ego mode.
  const [entryY, exitY] = [lowerFloorTop, upperFloorTop];
  // Subtract centring offset just like the tread positions do.
  const cx = -body.footprintWidth / 2;
  const cz = -body.footprintDepth / 2;
  const corners = (() => {
    switch (stair.climbDir) {
      // plusX: entry edge at x=sx (low x), exit edge at x=sx+sw (high x).
      // CCW from +Y: low-x/low-z → low-x/high-z → high-x/high-z → high-x/low-z.
      case 'plusX':
        return [
          { x: cx + sx,       y: entryY, z: cz + sz },
          { x: cx + sx,       y: entryY, z: cz + sz + sd },
          { x: cx + sx + sw,  y: exitY,  z: cz + sz + sd },
          { x: cx + sx + sw,  y: exitY,  z: cz + sz },
        ];
      // minusX: entry at x=sx+sw (high x), exit at x=sx (low x).
      // CCW from +Y: low-x/low-z → low-x/high-z → high-x/high-z → high-x/low-z
      // — same spatial order as plusX, just flipping which end is entry.
      case 'minusX':
        return [
          { x: cx + sx,       y: exitY,  z: cz + sz },
          { x: cx + sx,       y: exitY,  z: cz + sz + sd },
          { x: cx + sx + sw,  y: entryY, z: cz + sz + sd },
          { x: cx + sx + sw,  y: entryY, z: cz + sz },
        ];
      // plusZ: entry at z=sz (low z), exit at z=sz+sd (high z).
      // CCW from +Y: low-x/low-z → low-x/high-z → high-x/high-z → high-x/low-z.
      case 'plusZ':
        return [
          { x: cx + sx,       y: entryY, z: cz + sz },
          { x: cx + sx,       y: exitY,  z: cz + sz + sd },
          { x: cx + sx + sw,  y: exitY,  z: cz + sz + sd },
          { x: cx + sx + sw,  y: entryY, z: cz + sz },
        ];
      // minusZ: entry at z=sz+sd (high z), exit at z=sz (low z).
      case 'minusZ':
        return [
          { x: cx + sx,       y: entryY, z: cz + sz + sd },
          { x: cx + sx + sw,  y: entryY, z: cz + sz + sd },
          { x: cx + sx + sw,  y: exitY,  z: cz + sz },
          { x: cx + sx,       y: exitY,  z: cz + sz },
        ];
    }
  })();
  void lateralLen; // width is folded into corners via sw/sd above
  // Winding: top face (0,1,2,3) CCW when viewed from +Y. Bottom face
  // mirrors it (4,5,6,7) at y=entryY, wound CW from +Y so its normals
  // point down. Sides connect top↔bottom with consistent outward
  // normals. All index triples below are CCW-outward.
  const positions = new Float32Array(8 * 3);
  for (let i = 0; i < 4; i++) {
    positions[i * 3 + 0] = corners[i].x;
    positions[i * 3 + 1] = corners[i].y;
    positions[i * 3 + 2] = corners[i].z;
    // Bottom vertex i+4: same XZ, y = lowerFloorTop - ε.
    // ε of 0.02 keeps the ramp solid so a falling capsule can't pass
    // through the thin top skin on a fast frame.
    positions[(i + 4) * 3 + 0] = corners[i].x;
    positions[(i + 4) * 3 + 1] = lowerFloorTop - 0.02;
    positions[(i + 4) * 3 + 2] = corners[i].z;
  }
  const indices = new Uint16Array([
    // Top (sloped) face
    0, 1, 2,
    0, 2, 3,
    // Bottom face (wound opposite of top)
    4, 6, 5,
    4, 7, 6,
    // Side 0-1 / 4-5
    0, 4, 5,
    0, 5, 1,
    // Side 1-2 / 5-6
    1, 5, 6,
    1, 6, 2,
    // Side 2-3 / 6-7
    2, 6, 7,
    2, 7, 3,
    // Side 3-0 / 7-4
    3, 7, 4,
    3, 4, 0,
  ]);
  const geom = new BufferGeometry();
  geom.setAttribute('position', new BufferAttribute(positions, 3));
  geom.setIndex(new BufferAttribute(indices, 1));
  geom.computeVertexNormals();
  // BasicMaterial with `visible=false` on the mesh keeps the proxy
  // invisible, while still letting the octree traversal reach its
  // triangles (see `collisionProxy` handling in collisionOctree.ts).
  const mat = new MeshBasicMaterial({ visible: false });
  const mesh = new Mesh(geom, mat);
  mesh.name = `StairRamp_to_L${args.stair.toLevel}`;
  mesh.visible = false;
  mesh.userData.collisionProxy = true;
  return mesh;
}
